'use client';

import { loadAnalyticsScript, SENSORS_SCRIPT_URL } from '../analyticsLoader';
import {
  advanceAnalyticsIdentity,
  captureTrackingEvent,
  type AnalyticsIdentityCommand,
  type CapturedEventInput,
} from '../earlyEvents';
import { User } from '@/api/types/public';
import { getZeeloolSnowplowClient } from '@/reporting/integrations/snowplow-sdk/client';
import logger from '@/utils/client-logger';
import { ZL_USERINFO } from '@/utils/constants';
import { env } from '@/utils/env';
import { getPlatformLanguage } from '@/utils/locale-cookie';
import { isInZeloolApp } from '@/utils/tools';
import { Provider, ProviderType, TrackingEvent } from '../types';
import { syncSensorsIdentityToStorage, type SensorsIdentity } from '../utils/sensorsIdentity';

/** 神策初始化所需的显式配置。 */
interface SensorsConfig {
  serverUrl: string;
  showLog: boolean;
  platform: string;
  platformName: string;
  envName: string;
  isEnabled?: boolean;
}

/** 将可选 SDK user ID 规范化为空值安全的字符串。 */
function normalizeSnowplowUserId(userId?: string): string | null {
  // nullish 值不应被猜测为 Sensors uuid。
  if (userId === null || userId === undefined) return null;

  const normalizedUserId = String(userId).trim();
  const lowerCaseUserId = normalizedUserId.toLowerCase();

  // 空字符串与字符串 nullish 值都不能进入 SDK identity。
  if (!normalizedUserId || lowerCaseUserId === 'null' || lowerCaseUserId === 'undefined') {
    return null;
  }

  return normalizedUserId;
}

/** 当前 1.26.18 默认渠道列表；本站没有额外 source_type 配置。 */
const SENSORS_UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
/** 神策默认搜索/社交来源匹配保持原版本语义。 */
const SENSORS_SEARCH_SOURCES = [
  'www.baidu.',
  'm.baidu.',
  'm.sm.cn',
  'so.com',
  'sogou.com',
  'youdao.com',
  'google.',
  'yahoo.com/',
  'bing.com/',
  'ask.com/',
];
/** 神策默认社交来源匹配。 */
const SENSORS_SOCIAL_SOURCES = [
  'weibo.com',
  'renren.com',
  'kaixin001.com',
  'douban.com',
  'qzone.qq.com',
  'zhihu.com',
  'tieba.baidu.com',
  'weixin.qq.com',
];

/** 使用发生时 URL/referrer 复现固定 SDK 的默认流量分类。 */
function historicalTrafficSource(url: string, referrer: string): string {
  if (new RegExp(`(${SENSORS_UTM_KEYS.join('|')})\\=[^&]+`).test(url)) return '付费广告流量';
  const source = referrer.split('?')[0];
  if (SENSORS_SEARCH_SOURCES.some((value) => source.includes(value))) return '自然搜索流量';
  if (SENSORS_SOCIAL_SOURCES.some((value) => source.includes(value))) return '社交网站流量';
  return referrer ? '引荐流量' : '直接流量';
}

/** 不调用会回退当前 document.referrer 的内部 helper，显式处理空来源。 */
function historicalSearchKeyword(referrer: string): string {
  if (!referrer) return '未取到值_直接打开';
  if (!referrer.startsWith('http')) return '未取到值_非http的url';
  try {
    const url = new URL(referrer);
    const engines: [RegExp, string[]][] = [
      [/^.*\.baidu\.com$/, ['wd', 'word', 'kw', 'keyword']],
      [/^.*\.bing\.com$/, ['q']],
      [/^www\.google\.(?:com|com\.[a-z]{2}|[a-z]{2})$/, ['q']],
      [/^m\.sm\.cn$/, ['q']],
      [/^.+\.so\.com$/, ['q']],
      [/^.*\.sogou\.com$/, ['query', 'keyword']],
      [/^.*\.yahoo\.com$/, ['p']],
    ];
    const keys = engines.find(([host]) => host.test(url.hostname))?.[1] ?? [];
    for (const key of keys) {
      const value = url.searchParams.get(key);
      if (value) return value;
    }
  } catch {
    /* 非法来源按原 SDK 的未取到值处理，不读取当前页面。 */
  }
  return '未取到值';
}

/** final properties plugin 在 Date 转换之后运行，必须提供本地时间字符串。 */
function historicalProfileTime(timestamp: number): string {
  const date = new Date(timestamp);
  const pad = (value: number, digits = 2) => String(value).padStart(digits, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}.${pad(date.getMilliseconds(), 3)}`;
}

/** document 范围身份屏障，多个 provider/remount 共享一次 SDK 初始化。 */
interface SensorsDocumentState {
  init?: Promise<boolean>;
  sdkInitialized: boolean;
  failed?: boolean;
  ready: Promise<SensorsIdentity>;
  resolve: (identity: SensorsIdentity) => void;
  reject: (error: Error) => void;
  identity?: SensorsIdentity;
  captured?: CapturedEventInput;
}
/** WeakMap 避免跨 SSR 请求或不同文档共用用户身份。 */
const sensorsDocuments = new WeakMap<Window, SensorsDocumentState>();

/** 明确失败后只重建等待屏障，保留 SDK 和已注册公共属性实例。 */
function resetSensorsReady(state: SensorsDocumentState): void {
  state.ready = new Promise<SensorsIdentity>((resolve, reject) => {
    state.resolve = resolve;
    state.reject = reject;
  });
  state.failed = false;
  state.init = undefined;
  void state.ready.catch(() => undefined);
}

/** 取得浏览器文档的稳定身份 Promise。 */
function getDocumentState(): SensorsDocumentState {
  let state = sensorsDocuments.get(window);
  if (!state) {
    let resolve!: SensorsDocumentState['resolve'];
    let reject!: SensorsDocumentState['reject'];
    const ready = new Promise<SensorsIdentity>((accept, fail) => {
      resolve = accept;
      reject = fail;
    });
    void ready.catch(() => undefined);
    state = { ready, resolve, reject, sdkInitialized: false };
    sensorsDocuments.set(window, state);
  }
  if (state.failed) resetSensorsReady(state);
  return state;
}

/** Snowplow 只等待当前文档 SDK 公开 ready API 验证出的真实匿名身份。 */
export function getSensorsReady(): Promise<SensorsIdentity> {
  if (typeof window === 'undefined') return Promise.reject(new Error('sensors_no_document'));
  return getDocumentState().ready;
}

/** 神策上报 Provider，并负责登录后同步 SDK 用户 ID。 */
export class SensorsProvider implements Provider {
  /** ReportingCenter 使用的 Provider 类型。 */
  type = ProviderType.SENSORS;

  /** 当前 Provider 是否启用。 */
  isEnabled: boolean;

  /** 是否输出神策调试日志。 */
  loggingEnabled = false; // 公开属性，支持ReportingCenter访问

  /** 神策 SDK 是否已经完成初始化。 */
  private initialized = false;

  /** 当前实例使用的神策配置。 */
  private config: SensorsConfig;

  /** 保存显式配置并解析本地日志开关。 */
  constructor(config: SensorsConfig) {
    this.config = config;
    this.isEnabled = config.isEnabled ?? true;
    this.loggingEnabled = env.NEXT_PUBLIC_ENV === 'local';
  }

  /** 初始化只允许单文档执行一次；网络等待不会阻塞 React 渲染。 */
  async init(): Promise<boolean> {
    if (typeof window === 'undefined' || !this.isEnabled) return false;
    const state = getDocumentState();
    const initialization = (state.init ??= this.initializeDocument(state));
    this.initialized = await initialization;
    // 明确加载失败允许重建 Promise；下载重试总预算仍由 document loader 控制。
    if (!this.initialized && state.init === initialization) state.init = undefined;
    return this.initialized;
  }

  /** 获取公共 ready 回调已确认的身份，用于门面身份阶段绑定。 */
  getIdentity(): SensorsIdentity {
    if (!this.initialized) return {};
    try {
      const sensors = window.sensorsDataAnalytic201505;
      const anonymousValue = sensors?.quick?.('getAnonymousID');
      const anonymousId =
        typeof anonymousValue === 'string' ? normalizeSnowplowUserId(anonymousValue) : null;
      const distinctId = normalizeSnowplowUserId(sensors?.store?.getDistinctId?.());
      return anonymousId ? { anonymousId, distinctId: distinctId ?? undefined } : {};
    } catch {
      return {};
    }
  }

  /** 初始化配置、公共属性在身份屏障之前完成，禁止自动生成当前页 PV。 */
  private async initializeDocument(state: SensorsDocumentState): Promise<boolean> {
    try {
      if (!window.sensorsDataAnalytic201505) {
        await loadAnalyticsScript({ id: 'sensors-sdk', src: SENSORS_SCRIPT_URL });
      }
      const sensors = window.sensorsDataAnalytic201505;
      if (!sensors || typeof sensors.init !== 'function' || typeof sensors.quick !== 'function') {
        throw new Error('sensors_api_unavailable');
      }
      window.sensors = sensors;
      if (!state.sdkInitialized) {
        // 关闭晚安装的自动事件，统一由早期门面保存原页面、时间和访问顺序。
        sensors.init({
          server_url: this.config.serverUrl,
          app_js_bridge: isInZeloolApp(),
          is_track_single_page: false,
          use_client_time: true,
          send_type: 'beacon',
          heatmap: { clickmap: 'not_collect', scroll_notice_map: 'default' },
          show_log: this.config.showLog,
          batch_send: { datasend_timeout: 6000, send_interval: 800, storage_length: 1000 },
        });
        state.sdkInitialized = true;
        this.registerCommonProperties(sensors);
        // autoTrack 保留首次 profile 副作用；最终插件仅校正本次历史首访归因。
        sensors.registerPropertyPlugin({
          /** 真实 1.26.18 在最终 properties 阶段同步提供 profile_set_once。 */
          properties(data: { type: string; time?: number; properties: Record<string, unknown> }) {
            const captured = state.captured;
            if (
              !captured ||
              data.type !== 'profile_set_once' ||
              !Object.prototype.hasOwnProperty.call(data.properties, '$first_visit_time')
            )
              return;
            const url = new URL(captured.page.url);
            for (const key of Object.keys(data.properties)) {
              if (key.startsWith('$utm_')) delete data.properties[key];
            }
            for (const key of SENSORS_UTM_KEYS) {
              const value = url.searchParams.get(key);
              if (value) data.properties[`$${key}`] = value;
            }
            data.time = captured.occurredAt;
            Object.assign(data.properties, {
              $first_visit_time: historicalProfileTime(captured.occurredAt),
              $first_referrer: captured.page.referrer,
              $first_referrer_host: captured.page.referrer
                ? new URL(captured.page.referrer).host
                : '',
              $first_traffic_source_type: historicalTrafficSource(
                captured.page.url,
                captured.page.referrer,
              ),
              $first_search_keyword: historicalSearchKeyword(captured.page.referrer),
            });
          },
        });
      }
      // isReady 是公开版本合同，load 或全局对象存在都不足以放行。
      const identity = await new Promise<SensorsIdentity>((resolve, reject) => {
        const deadline = setTimeout(
          () => logger.warn('[SensorsProvider] identity_degraded'),
          60_000,
        );
        sensors.quick('isReady', () => {
          clearTimeout(deadline);
          try {
            const anonymousValue = sensors.quick('getAnonymousID');
            const anonymousId =
              typeof anonymousValue === 'string' ? normalizeSnowplowUserId(anonymousValue) : null;
            if (!anonymousId) {
              reject(new Error('sensors_identity_unavailable'));
              return;
            }
            const distinctId = normalizeSnowplowUserId(sensors.store?.getDistinctId?.());
            resolve({ anonymousId, distinctId: distinctId ?? undefined });
          } catch {
            reject(new Error('sensors_identity_unavailable'));
          }
        });
      });
      syncSensorsIdentityToStorage(identity);
      state.identity = identity;
      state.resolve(identity);
      return true;
    } catch {
      state.failed = true;
      state.reject(new Error('sensors_initialization_failed'));
      logger.error('[SensorsProvider] initialization_failed');
      return false;
    }
  }

  /** 历史快照直接覆盖动态公共属性；接收仅代表神策 API 交接成功。 */
  trackCaptured(input: CapturedEventInput): { accepted: boolean; reason?: string } {
    if (!this.initialized || !this.isEnabled) return { accepted: false, reason: 'not_ready' };
    const sensors = window.sensorsDataAnalytic201505;
    if (
      !input.identity.anonymousId ||
      input.identity.anonymousId !== this.getIdentity().anonymousId
    ) {
      return { accepted: false, reason: 'identity_unresolvable' };
    }
    const state = getDocumentState();
    try {
      const url = new URL(input.page.url);
      const attributionValid =
        typeof input.attribution.expires === 'number' &&
        input.attribution.expires > input.occurredAt;
      const properties: Record<string, unknown> = {
        pub_member_level: input.user.memberLevel ?? '9999',
        pub_is_paid: input.user.isPaid ?? false,
        pub_is_login: input.user.isLoggedIn ?? false,
        pub_platform_language: input.platformLanguage ?? '',
        ...Object.fromEntries(
          [
            'pub_zl_utm_source',
            'pub_zl_utm_medium',
            'pub_zl_utm_campaign',
            'pub_zl_utm_campaignid',
            'pub_zl_referrer',
            'pub_zl_page',
            'pub_landing_page',
          ].map((key) => [
            key,
            key === 'pub_landing_page' ||
            attributionValid ||
            (key === 'pub_zl_page' && input.attribution.expires === -1)
              ? (input.attribution[key] ?? '')
              : '',
          ]),
        ),
        ...(input.event.kind === 'signUp' ? {} : input.event.properties),
        $time: new Date(input.occurredAt),
        $url: input.page.url,
        $url_path: url.pathname,
        $title: input.page.title,
        $referrer: input.page.referrer,
        $referrer_host: input.page.referrer ? new URL(input.page.referrer).host : '',
        sensors_event_id:
          input.event.kind === 'track'
            ? (input.event.options?.sensorsEventId ?? input.logicalEventId)
            : input.logicalEventId,
      };
      // URL 渠道的快照覆盖 SDK 当前页面渠道，避免快速跳页后的归因漂移。
      for (const key of SENSORS_UTM_KEYS) {
        properties[`$${key}`] = url.searchParams.get(key) ?? '';
        properties[`$latest_${key}`] =
          properties[`pub_zl_${key}`] || url.searchParams.get(key) || '';
      }
      properties.$latest_referrer = properties.pub_zl_referrer || input.page.referrer;
      properties.$latest_referrer_host = properties.$latest_referrer
        ? new URL(String(properties.$latest_referrer)).host
        : '';
      // Sensors heatmap 的预置元素字段带 $ 前缀，早期捕获需保持同一载荷合同。
      if (input.event.kind === 'webClick') {
        for (const [key, value] of Object.entries(input.event.properties)) {
          if (key.startsWith('element_')) {
            properties[`$${key}`] = value;
            delete properties[key];
          }
        }
      }
      properties.$latest_traffic_source_type = historicalTrafficSource(
        input.page.url,
        input.page.referrer,
      );
      properties.$latest_search_keyword = historicalSearchKeyword(input.page.referrer);
      state.captured = input;
      if (input.event.kind === 'pageView') {
        sensors.quick('autoTrack', properties);
      } else if (input.event.kind === 'signUp') {
        // 注册仍使用专用业务输入，禁止将用户对象展开成任意敏感属性。
        sensors.track('SignUp', properties);
      } else {
        const name =
          input.event.kind === 'track'
            ? input.event.eventName
            : { webClick: '$WebClick', webExp: 'WebExp', pageLeave: '$WebPageLeave' }[
                input.event.kind
              ];
        sensors.track(name, properties);
      }
      return { accepted: true };
    } catch {
      // SDK 调用抛错可能发生在交接之后，门面不得盲目重放未知结果。
      logger.error('[SensorsProvider] captured_dispatch_unknown');
      return { accepted: false, reason: 'dispatch_unknown' };
    } finally {
      state.captured = undefined;
    }
  }

  /** 身份队列按发生顺序执行原有神策登录/退出行为。 */
  applyIdentity(command: AnalyticsIdentityCommand): boolean {
    if (!this.initialized) return false;
    if (command.kind === 'login') return this.applyLogin(command.uuid, command.userId);
    try {
      window.sensorsDataAnalytic201505.setProfile({ pub_is_login: false });
      return true;
    } catch {
      logger.error('[SensorsProvider] logout_failed');
      return false;
    }
  }

  /** 旧 Provider 入口也先捕获快照，SDK 未 ready 时由同一门面持有。 */
  track(event: TrackingEvent): void {
    captureTrackingEvent(event, [ProviderType.SENSORS]);
  }

  /** 退出身份动作交给共享队列，不抢先改变历史事件阶段。 */
  sensorsLogout(): void {
    advanceAnalyticsIdentity({ kind: 'logout' });
  }

  /** 登录立即确认门面接收，分析身份执行由有序控制流负责。 */
  sensorsLogin(uuid: string, snowplowUserId?: string): boolean {
    const normalizedUuid = normalizeSnowplowUserId(uuid);
    if (!normalizedUuid) return false;
    return advanceAnalyticsIdentity({ kind: 'login', uuid: normalizedUuid, userId: snowplowUserId })
      .accepted;
  }

  /**
   * 调用神策登录，回读确认身份绑定成功后再同步显式 SDK user ID。
   */
  private applyLogin(uuid: string, snowplowUserId?: string): boolean {
    try {
      const normalizedUuid = uuid.trim();
      if (!normalizedUuid) return false;

      window.sensorsDataAnalytic201505.login(normalizedUuid);
      const identity = this.syncIdentity();
      if (identity.distinctId !== normalizedUuid) return false;

      this.syncSnowplowUserId(snowplowUserId);
      return true;
    } catch (error) {
      logger.log('sensors login error', error);
      return false;
    }
  }

  /** 返回神策 Provider 初始化状态。 */
  isInitialized(): boolean {
    return this.initialized;
  }

  /**
   * 仅把显式 customer id 同步给 SDK，失败不改变神策登录成功结果。
   */
  private syncSnowplowUserId(userId?: string): void {
    const normalizedUserId = normalizeSnowplowUserId(userId);

    // 支付静默链只传 uuid 时不得猜测或设置 SDK user ID。
    if (!normalizedUserId) return;

    try {
      getZeeloolSnowplowClient()?.setUserId(normalizedUserId);
    } catch {
      // 日志只记录稳定错误类别，不携带 SDK 异常或身份值。
      logger.error('[SensorsProvider] snowplow_set_user_id_failed');
    }
  }

  /**
   * 将神策 SDK 当前身份同步到本地缓存，供 Snowplow 首屏事件兜底读取。
   */
  private syncIdentity(): ReturnType<typeof syncSensorsIdentityToStorage> {
    return syncSensorsIdentityToStorage();
  }

  /** 注册神策按事件实时读取的公共属性。 */
  private registerCommonProperties(sensors: any): void {
    // const latestChannelAttribute = this.getLatestChannelAttribute();
    const params = {
      pub_platform: this.config.platform,
      pub_platform_name: this.config.platformName,
      pub_platform_language: () => getPlatformLanguage(),
      pub_client: this.config.platform,
      pub_env_name: () => {
        // 区分灰度的新服务还是旧服务
        if (this.config.envName === 'production') return 'production_is_nextjs';
        return this.config.envName;
      },
      pub_member_level: () => {
        let userInfo: User = {};
        try {
          userInfo = JSON.parse(localStorage.getItem(ZL_USERINFO) || '{}') || {};
        } catch (error) {
          logger.error('Failed to parse user info:', error);
        }
        return `${userInfo.member_level >= -1 ? userInfo.member_level : '9999'}`;
      },
      pub_is_paid: () => {
        let userInfo: User = {};
        try {
          userInfo = JSON.parse(localStorage.getItem(ZL_USERINFO) || '{}') || {};
        } catch (error) {
          logger.error('Failed to parse user info:', error);
        }
        return !!userInfo.last_pay_time;
      },
      pub_is_login: () => {
        try {
          return !!localStorage.getItem('zl-token');
        } catch (error) {
          logger.error('Failed to parse user info:', error);
          return false;
        }
      },
      pub_zl_utm_source: () => {
        if (window.latestChannelAttribute?.expires > Date.now()) {
          return window.latestChannelAttribute?.pub_zl_utm_source || undefined;
        }
        return undefined;
      },
      pub_zl_utm_medium: () => {
        if (window.latestChannelAttribute?.expires > Date.now()) {
          return window.latestChannelAttribute?.pub_zl_utm_medium || undefined;
        }
        return undefined;
      },
      pub_zl_utm_campaign: () => {
        if (window.latestChannelAttribute?.expires > Date.now()) {
          return window.latestChannelAttribute?.pub_zl_utm_campaign || undefined;
        }
        return undefined;
      },
      pub_zl_utm_campaignid: () => {
        if (window.latestChannelAttribute?.expires > Date.now()) {
          return window.latestChannelAttribute?.pub_zl_utm_campaignid || undefined;
        }
        return undefined;
      },
      pub_zl_referrer: () => {
        if (window.latestChannelAttribute?.expires > Date.now()) {
          return window.latestChannelAttribute?.pub_zl_referrer || undefined;
        }
        return undefined;
      },
      pub_zl_page: () => {
        if (
          window.latestChannelAttribute?.expires > Date.now() ||
          window.latestChannelAttribute?.expires === -1
        ) {
          return window.latestChannelAttribute?.pub_zl_page || undefined;
        }
        return undefined;
      },
      pub_landing_page: () => {
        return window.latestChannelAttribute?.pub_landing_page || undefined;
      },
    };
    for (const key in params) {
      // 虽说不再报警告了但是值失去了实时性
      // if (typeof params[key] === 'function') {
      //   params[key] = params[key]();
      // }
      if (params[key] === undefined || params[key] === null || params[key] === '') {
        delete params[key];
      }
    }
    sensors.registerPage(params);
  }

  /**
   * 启用Sensors日志
   */
  enableLog(): void {
    this.loggingEnabled = true;
  }

  /**
   * 禁用Sensors日志
   */
  disableLog(): void {
    this.loggingEnabled = false;
  }
}
